import { DownloadCloud01 } from "@untitled-ui/icons-react";
import { useState } from "react";
import {
	getSignedPutUrl,
	registerS3File,
	uploadFileToS3,
} from "../../../api/queries/files.queries";
import FileCard from "./FileCard";

type FileDropUploadProps = {
	readonly bucket?: "private" | "public";
	readonly folderId?: string;
	readonly existingFileIds?: string[];
	readonly onUploadedFiles?: (fileIds: string[]) => void;
	readonly onDeleteOneFile?: (fileId: string) => void;
	readonly disabled?: boolean;
};

const MAX_FILE_SIZE = 100 * 1024 * 1024;

export default function FileDropUpload({
	bucket = "private",
	folderId,
	existingFileIds = [],
	onDeleteOneFile,
	onUploadedFiles,
	disabled,
}: FileDropUploadProps) {
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [fileIds, setFileIds] = useState<string[]>(existingFileIds);

	const uploadSingleFile = async (file: File): Promise<string> => {
		if (file.size > MAX_FILE_SIZE) {
			throw new Error(
				`Le fichier ${file.name} dépasse la taille maximale de 100 MB`,
			);
		}

		const signed = await getSignedPutUrl({
			bucket: bucket,
			contentType: file.type,
		});

		await uploadFileToS3(signed.signedUrl, file);

		const registeredFile = await registerS3File({
			bucket: bucket,
			key: signed.key,
			name: file.name,
			mimeType: file.type,
			size: file.size,
			folderId,
		});

		return registeredFile.id;
	};

	const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
		setError(null);
		const files = e.target.files;
		if (!files || files.length === 0) return;

		setUploading(true);

		try {
			const allUploadedIds: string[] = [];

			for (const file of Array.from(files)) {
				const fileId = await uploadSingleFile(file);
				allUploadedIds.push(fileId);
			}

			setFileIds((prev) => [...prev, ...allUploadedIds]);
			onUploadedFiles?.(allUploadedIds);
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Erreur lors de l'upload";
			setError(errorMessage);
		} finally {
			setUploading(false);
			e.target.value = "";
		}
	};

	const handleRemove = (fileIdToRemove: string) => {
		setFileIds((previousFileIds) =>
			previousFileIds.filter((fileId) => fileId !== fileIdToRemove),
		);
		onDeleteOneFile?.(fileIdToRemove);
	};

	return (
		<div className="w-full">
			<label
				className={`block w-full ${
					uploading || disabled
						? "cursor-not-allowed opacity-60"
						: "cursor-pointer"
				}`}
			>
				<div
					className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 transition-colors ${
						uploading || disabled
							? "bg-gray-100 border-gray-200"
							: "bg-white hover:bg-accent/50"
					}`}
				>
					<DownloadCloud01
						className={`w-8 h-8 mb-2 ${
							uploading || disabled ? "text-gray-300" : "text-blue-400"
						}`}
					/>
					<span
						className={`font-semibold ${
							uploading || disabled ? "text-gray-400" : "text-text-blue"
						}`}
					>
						Cliquez pour ajouter
					</span>{" "}
					ou glisser-déposer des fichiers (tous formats supportés)
					<span className="text-sm text-gray-500 mt-1">
						Taille maximale : 100 MB par fichier
					</span>
					<input
						type="file"
						multiple
						className="hidden"
						onChange={handleFiles}
						disabled={uploading || disabled}
					/>
				</div>
			</label>
			{error && <div className="text-red-500 mt-2">{error}</div>}
			{uploading && (
				<div className="mt-2 text-blue-500">
					Upload des fichiers en cours...
				</div>
			)}
			{fileIds.length > 0 && (
				<div className="mt-8">
					<div className="flex items-center justify-between mb-4">
						<h3 className="text-lg font-medium text-gray-900">
							Fichiers uploadés ({fileIds.length})
						</h3>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						{fileIds.map((fileId) => (
							<FileCard
								key={fileId}
								id={fileId}
								onRemove={handleRemove}
								readonly={disabled}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
